import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { camelToNormal } from "../../common/caseconversion";
import Loading from "../../common/Laoding";

// Fix imports - ensure these are properly exported from their files
import CommonColumnjson from "../../common/columncommon.json";
import VehicleUtilizationChart from "./VehicleUtilizationChart";
import VehicleTrendGrid from "./VehicleTrendGrid";
import FigmaDataWorkspace from "./FigmaDataWorkspace";

// Add this function after imports and before the KYV component
const enhanceColumnsWithData = (keyType, baseColumns, rowData) => {
  if (!baseColumns || baseColumns.length === 0) return baseColumns;
  if (!rowData || rowData.length === 0) return baseColumns;
  
  // Get the first row to check available fields
  const sampleRow = rowData[0];
  const enhancedColumns = [...baseColumns];
  
  // Helper to check if a field exists in the data
  const fieldExists = (fieldNames) => {
    return fieldNames.some(field => sampleRow[field] !== undefined);
  };
  
  // Helper to get value from multiple possible field names
  const getValue = (row, fieldNames) => {
    for (const field of fieldNames) {
      if (row[field] !== undefined && row[field] !== null) {
        return row[field];
      }
    }
    return null;
  };
  
  // For Bookings tab - add booking status
  if (keyType === 'vehicleBookings') {
    const hasStatus = enhancedColumns.some(col => 
      col.field === 'bookingStatus' || col.field === 'status'
    );
    
    if (!hasStatus && fieldExists(['bookingStatus', 'status', 'booking_status'])) {
      enhancedColumns.push({
        field: 'bookingStatus',
        headerName: 'Booking Status',
        width: 150,
        renderCell: (params) => {
          const status = getValue(params.row, ['bookingStatus', 'status', 'booking_status']) || 'N/A';
          const statusColors = {
            'CONFIRMED': { bg: '#DCFCE7', text: '#15803D', label: 'Confirmed' },
            'PENDING': { bg: '#FEF3C7', text: '#B45309', label: 'Pending' },
            'CANCELLED': { bg: '#FEE2E2', text: '#DC2626', label: 'Cancelled' },
            'COMPLETED': { bg: '#DBEAFE', text: '#1D4ED8', label: 'Completed' },
            'IN_PROGRESS': { bg: '#E9D5FF', text: '#6D28D9', label: 'In Progress' },
            'APPROVED': { bg: '#D1FAE5', text: '#047857', label: 'Approved' },
            'REJECTED': { bg: '#FEE2E2', text: '#DC2626', label: 'Rejected' },
          };
          const style = statusColors[status] || { bg: '#F3F4F6', text: '#6B7280', label: status };
          return (
            <span style={{
              background: style.bg,
              color: style.text,
              padding: '2px 10px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: '500',
              fontFamily: 'Outfit, sans-serif',
              display: 'inline-block'
            }}>
              {style.label}
            </span>
          );
        }
      });
    }
  }
  
  // For Documents tab - add document type
  if (keyType === 'vehicleDocuments') {
    const hasDocType = enhancedColumns.some(col => 
      col.field === 'documentType' || col.field === 'docType' || col.field === 'type'
    );
    
    if (!hasDocType && fieldExists(['documentType', 'docType', 'type', 'document_type'])) {
      enhancedColumns.push({
        field: 'documentType',
        headerName: 'Document Type',
        width: 150,
        renderCell: (params) => {
          const docType = getValue(params.row, ['documentType', 'docType', 'type', 'document_type']) || 'N/A';
          return (
            <span style={{
              fontSize: '13px',
              fontWeight: '500',
              color: '#1A1917',
              fontFamily: 'Outfit, sans-serif'
            }}>
              {docType}
            </span>
          );
        }
      });
    }
  }
  
  // For Fuel Logs tab - add fuel type
  if (keyType === 'fuelLogs') {
    const hasFuelType = enhancedColumns.some(col => 
      col.field === 'fuelType' || col.field === 'fuel_type' || col.field === 'type'
    );
    
    if (!hasFuelType && fieldExists(['fuelType', 'fuel_type', 'type'])) {
      enhancedColumns.push({
        field: 'fuelType',
        headerName: 'Fuel Type',
        width: 130,
        renderCell: (params) => {
          const fuelType = getValue(params.row, ['fuelType', 'fuel_type', 'type']) || 'N/A';
          const fuelColors = {
            'PETROL': { bg: '#DBEAFE', text: '#1D4ED8' },
            'DIESEL': { bg: '#FEF3C7', text: '#B45309' },
            'CNG': { bg: '#DCFCE7', text: '#15803D' },
            'EV': { bg: '#E9D5FF', text: '#6D28D9' },
            'ELECTRIC': { bg: '#E9D5FF', text: '#6D28D9' },
          };
          const style = fuelColors[fuelType] || { bg: '#F3F4F6', text: '#6B7280' };
          return (
            <span style={{
              background: style.bg,
              color: style.text,
              padding: '2px 10px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: '500',
              fontFamily: 'Outfit, sans-serif',
              display: 'inline-block'
            }}>
              {fuelType}
            </span>
          );
        }
      });
    }
  }
  
  return enhancedColumns;
};

// FUTURE-PROOF HUB CONFIGURATION DICTIONARY
// const CITY_HUB_MAPPING = {
//   "1": ["Btm 4th stage"],
//   "2": ["Mysore"],
//   "3": ["Vellore"],
//   "4": ["Chennai"],
// };

const KYV = ({ selectedRow }) => {
  const OUTFIT = "Outfit, sans-serif";
  const MONO = "DM Mono, monospace";
  const cities = useSelector((state) => state?.cities?.citiesData);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  // Helper function to get hub label - using only cities data
  const getCityHubLabel = (activeCity) => {
    if (!activeCity || !cities || !cities[activeCity]) {
      return "Regional Fleet Hub";
    }

    // Just return the city name as hub
    return `${cities[activeCity]} Hub`;
  };

  // Base systemic date helper calculations
  const getToday = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getOneYearAgo = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split("T")[0];
  };

  const todayMax = getToday();
  const oneYearAgoMin = getOneYearAgo();

  // Core Operational States
  const [vehicleApiData, setVehicleApiData] = useState([]);
  const [apiData, setApiData] = useState([]);
  const [vehicleId, setVehicleId] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [keyType, setKeyType] = useState("");
  const [city, setCity] = useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [VehicleUtilizationData, setVehicleUtilizationData] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleTrendGridApi, setVehicleTrendGridApi] = useState([]);
  const [totalCountForTrend, setTotalCountForTrend] = useState(0);

  // Dynamic Manager Endpoint Counts State
  const [entityCounts, setEntityCounts] = useState({
    bookingCount: 0,
    documentCount: 0,
    issueCount: 0,
    fuelLogCount: 0,
    workOrderCount: 0,
  });

  // Onboarding Setup Controls State
  const [showPopup, setShowPopup] = useState(true);
  const [popupStep, setPopupStep] = useState(1);
  const [popupFilters, setPopupFilters] = useState({
    fromDate: getOneYearAgo(),
    toDate: getToday(),
  });

  const [filters, setFilters] = useState({
    fromDate: getOneYearAgo(),
    toDate: getToday(),
    calculationType: "vehicleIssues",
  });

  // Fetch vehicle data when city changes
  // useEffect(() => {
  // //   if (city) {
  // //     setVehicleLoading(true);
  // //     getVehicleData();
  // //   }
  // // }, [city]);

  const getVehicleData = async () => {
    setVehicleLoading(true)
    try {
      const response = await fetch(
        `${apiUrl}/api/vehicles?limit=1000&cityId=${city}`,
        { method: "GET" },
      );
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setVehicleLoading(false);
        setVehicleApiData(data?.entities?.rows || []);
      }
      return data;
    } catch (error) {
      console.error("Error fetching get vehicle listings:", error);
      setVehicleLoading(false);
      return null;
    }
  };

  const getVehicleEntityCounts = async () => {
    if (!vehicleId) return;

    try {
      const formattedStart = `${filters.fromDate}T00:00`;
      const formattedEnd = `${filters.toDate}T23:59`;

      // Define all the entity types we need counts for
      const entityTypes = [
        { key: "vehicleBookings", countField: "bookingCount" },
        { key: "vehicleIssues", countField: "issueCount" },
        { key: "vehicleDocuments", countField: "documentCount" },
        { key: "fuelLogs", countField: "fuelLogCount" },
      ];

      // Make parallel API calls for each entity type
      const results = await Promise.allSettled(
        entityTypes.map(async (entity) => {
          try {
            const response = await fetch(
              `${apiUrl}/api/knowyourevehicle?vehicleId=${vehicleId}&key=${entity.key}&startDate=${formattedStart}&endDate=${formattedEnd}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  Authorization: `${token}`,
                },
              },
            );

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            // Extract count based on response structure
            let count = 0;
            if (data?.statusCode === "0100") {
              // For bookings, we might have totalBookings field
              if (
                entity.key === "vehicleBookings" &&
                data?.dataValues?.totalBookings !== undefined
              ) {
                count = data.dataValues.totalBookings;
              }
              // For other entities, count the rows array
              else if (data?.dataValues?.rows) {
                count = data.dataValues.rows.length;
              }
              // Fallback to checking if dataValues exists
              else if (data?.dataValues) {
                count = 1; // At least one record exists
              }
            }

            return { key: entity.key, count, success: true };
          } catch (error) {
            console.error(`Error fetching ${entity.key}:`, error);
            return { key: entity.key, count: 0, success: false };
          }
        }),
      );

      // Process the results
      const newCounts = {
        bookingCount: 0,
        documentCount: 0,
        issueCount: 0,
        fuelLogCount: 0,
        workOrderCount: 0,
      };

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.success) {
          const { key, count } = result.value;
          switch (key) {
            case "vehicleBookings":
              newCounts.bookingCount = count;
              break;
            case "vehicleIssues":
              newCounts.issueCount = count;
              break;
            case "vehicleDocuments":
              newCounts.documentCount = count;
              break;
            case "fuelLogs":
              newCounts.fuelLogCount = count;
              break;
            default:
              break;
          }
        }
      });

      setEntityCounts(newCounts);

      // Optional: Show success message only once
      console.log("Entity counts updated:", newCounts);
    } catch (error) {
      console.error("Failed fetching entity counts:", error);
      // Don't show error toast to avoid spamming
    }
  };

  const getVehicleBookingListings = async (page = 0, pageSize = 10) => {
  if (!vehicleId) {
    toast.error("Please select a vehicle first", {
      position: "bottom-center",
      style: {
        background: "#F0F0F0",
        color: "red",
        fontWeight: "medium",
        marginBottom: "50px",
      },
    });
    return;
  }

  try {
    setLoading(true);

    const formattedStart = `${filters.fromDate}T00:00`;
    const formattedEnd = `${filters.toDate}T23:59`;

    const response = await fetch(
      `${apiUrl}/api/knowyourevehicle?vehicleId=${vehicleId}&key=${keyType}&startDate=${formattedStart}&endDate=${formattedEnd}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
      },
    );

    const data = await response.json();

    // LOG THE DATA STRUCTURE TO SEE WHAT FIELDS ARE AVAILABLE
    console.log(`Data for ${keyType}:`, data);
    if (data?.dataValues?.rows && data.dataValues.rows.length > 0) {
      console.log(`First row fields for ${keyType}:`, Object.keys(data.dataValues.rows[0]));
    }

    if (data?.statusCode === "0100") {
      const rowData = data?.dataValues?.rows || [];
      setTotalCount(rowData.length);
      setApiData(rowData);
      setRows(rowData);
    } else {
      setTotalCount(0);
      setApiData([]);
      setRows([]);
      if (data?.message) {
        toast.error(data.message, { position: "bottom-center" });
      }
    }
  } catch (error) {
    console.error("Error fetching listings:", error);
    toast.error("Failed to fetch vehicle data", {
      position: "bottom-center",
    });
    setTotalCount(0);
    setApiData([]);
    setRows([]);
  } finally {
    setLoading(false);
  }
};

  const getVehicleUtilizationData = async () => {
    if (!vehicleId) return;

    try {
      const formattedStart = `${filters.fromDate}T00:00`;
      const formattedEnd = `${filters.toDate}T23:59`;
      const response = await fetch(
        `${apiUrl}/api/analytics/vehicle-wise?vehicleId=${vehicleId}&startDate=${formattedStart}&endDate=${formattedEnd}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();
      if (data?.statusCode === "0100") {
        setVehicleUtilizationData(data?.dataValues);
      }
    } catch (error) {
      console.error("Error fetching utilization data:", error);
      toast.error("Failed to fetch utilization data", {
        position: "bottom-center",
      });
    }
  };

  const handlePopupSubmit = async () => {
    if (
      popupFilters.fromDate < oneYearAgoMin ||
      popupFilters.toDate > todayMax
    ) {
      toast.error("Selected dates exceed maximum bounds limits", {
        position: "bottom-center",
      });
      return;
    }
    if (!city) {
      toast.error("Please select a city first", { position: "bottom-center" });
      return;
    }

    // setFilters((prev) => ({
    //   ...prev,
    //   fromDate: popupFilters.fromDate,
    //   toDate: popupFilters.toDate,
    // }));
    await getVehicleData();
    await getVehicleTrendData()    
    // handleSearch();
  };

  const handleSearch = async () => {
    if (!city) {
      toast.error("Please choose an operational city first", {
        position: "bottom-center",
      });
      return;
    }

    try {
      setIsLoading(true);
      setLoading(true);

      if (!vehicleId) {
        await getVehicleData();
        toast.success("City vehicle index updated successfully", {
          position: "bottom-center",
        });
      } else {
        await Promise.all([
          getVehicleUtilizationData(),
          getVehicleEntityCounts(),
          keyType
            ? getVehicleBookingListings(
                paginationModel.page,
                paginationModel.pageSize,
              )
            : Promise.resolve(),
        ]);

        toast.success("Metrics refetched successfully", {
          position: "bottom-center",
          style: { fontFamily: "Outfit", fontSize: "14px" },
        });
      }
    } catch (error) {
      console.error("Master dashboard data update batch run failed:", error);
      toast.error("Failed to complete asset refresh check", {
        position: "bottom-center",
      });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

      useEffect(() => {
    if (keyType && vehicleId) {
      getVehicleBookingListings(paginationModel.page, paginationModel.pageSize);
      const baseColumns = CommonColumnjson[keyType] || [];
      // Enhance columns with data from the current rows
      const enhancedColumns = enhanceColumnsWithData(keyType, baseColumns, rows);
      setColumns(enhancedColumns);
    }
  }, [
    keyType,
    vehicleId,
    paginationModel.page,
    paginationModel.pageSize,
    filters.fromDate,
    filters.toDate,
  ]);
    // Update columns when rows data changes (for dynamic fields)
  useEffect(() => {
    if (keyType && rows.length > 0) {
      const baseColumns = CommonColumnjson[keyType] || [];
      const enhancedColumns = enhanceColumnsWithData(keyType, baseColumns, rows);
      setColumns(enhancedColumns);
    }
  }, [rows, keyType]);
  

  useEffect(() => {
    if (vehicleId) {
      getVehicleUtilizationData();
      getVehicleEntityCounts();
    }
  }, [vehicleId, filters.fromDate, filters.toDate]);

  const getGridTitle = () => camelToNormal(keyType);

  const getStatusStyles = (status) => {
    if (status === "Verge Of Loss" || status === "VERGE_OF_LOSS") {
      return {
        bg: "bg-orange-50",
        text: "text-orange-600",
        border: "border-orange-200",
        dotBg: "bg-orange-500",
        label: "Verge Of Loss",
      };
    }
    if (
      status === "Loss" ||
      status === "Low Profit" ||
      status === "LOSS" ||
      status === "LOW_PROFIT"
    ) {
      return {
        bg: "bg-red-50",
        text: "text-red-600",
        border: "border-red-200",
        dotBg: "bg-red-500",
        label: status === "LOW_PROFIT" ? "Low Profit" : "Loss",
      };
    }
    if (
      status === "High Profit" ||
      status === "HIGH_PROFIT" ||
      status === "Profit" ||
      status === "PROFIT" ||
      status === "Moderate Profit" ||
      status === "MODERATE_PROFIT"
    ) {
      const displayLabel =
        status === "HIGH_PROFIT"
          ? "High Profit"
          : status === "MODERATE_PROFIT"
            ? "Moderate Profit"
            : "Profit";
      return {
        bg: "bg-green-50",
        text: "text-green-600",
        border: "border-green-200",
        dotBg: "bg-green-500",
        label: displayLabel,
      };
    }
    return {
      bg: "bg-stone-50",
      text: "text-stone-600",
      border: "border-stone-200",
      dotBg: "bg-stone-400",
      label: status,
    };
  };

  const handleViewClick = (row) => {
    if (keyType === "vehicleBookings") {
      window.open(
        `/viewallbookings?bookingid=${row?.bookingCode || row?.bookingId}&display=true`,
        "_blank",
      );
    } else if (keyType === "vehicleIssues") {
      window.open(`/viewallissues?issueId=${row?.id}&display=true`, "_blank");
    } else if (keyType === "vehicleDocuments") {
      window.open(
        `/vehiclesdashboard?vehicleId=${selectedVehicle?.licensePlate}&display=true`,
        "_blank",
      );
    } else if (keyType === "fuelLogs") {
      window.open(
        `/fuellogs?vehicleId=${selectedVehicle?.id}&display=true`,
        "_blank",
      );
    }
  };

  const getVehicleTrendData = async (cityId) => {
    try {
      setLoading(true);
      const formattedStart = `${filters.fromDate}T00:00`;
      const formattedEnd = `${filters.toDate}T23:59`;
      const response = await fetch(
        `${apiUrl}/api/vehicles/vehicletrend?cityId=${city}&startDate=${formattedStart}&endDate=${formattedEnd}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();

      if (data?.statusCode === "0100") {
        let apiRows = data?.entities || [];

        if (vehicleId) {
          apiRows = apiRows.filter(
            (item) => String(item?.id) === String(vehicleId),
          );
        }
        setShowPopup(false);
        setVehicleTrendGridApi(apiRows);
        setTotalCountForTrend(data?.entities?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching Vehicle Trend Data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add this function inside your component, after the getVehicleTrendData function
  const copyVehicleNumber = () => {
    if (!selectedVehicle?.licensePlate) {
      toast.error("No vehicle number to copy", { position: "bottom-center" });
      return;
    }
    
    navigator.clipboard.writeText(selectedVehicle.licensePlate)
      .then(() => {
        toast.success("Vehicle number copied!", { 
          position: "bottom-center",
          style: { fontFamily: "Outfit", fontSize: "14px" }
        });
      })
      .catch(() => {
        toast.error("Failed to copy vehicle number", { position: "bottom-center" });
      });
  };

  if (isLoading) return <Loading />;

  return (
    <div className="w-full bg-white my-4 flex flex-col justify-start items-start select-none font-['Outfit'] relative">
      {/* Popup Modal */}
      {showPopup &&
        createPortal(
          <div className="fixed inset-0 w-full h-full bg-stone-900/60 backdrop-blur-xs z-[999999] flex items-center justify-center pointer-events-auto overflow-hidden">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md border border-stone-200 shadow-2xl flex flex-col gap-5 relative z-[1000000] my-auto mx-auto">
              <div className="flex justify-between items-center border-b border-stone-100 pb-3">
                <span className="text-stone-900 font-bold text-lg">
                  Initialize Fleet Workspace
                </span>
                <span className="text-stone-400 text-xs font-semibold uppercase tracking-wider font-['DM_Mono']">
                  Step {popupStep === 3 ? 2 : popupStep} of 2
                </span>
              </div>

              {popupStep === 1 && (
                <div className="flex flex-col gap-3">
                  <label className="text-stone-700 text-sm font-semibold">
                    Select Operational Region
                  </label>
                  <div className="h-11 px-3.5 bg-white rounded-xl outline outline-[0.80px] outline-stone-200 flex items-center">
                    <select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full bg-transparent text-stone-900 text-sm font-medium focus:outline-none cursor-pointer"
                      aria-label="Select operational city"
                    >
                      <option value="">Choose a city layout...</option>
                      {Object.entries(cities || {}).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    disabled={!city}
                    onClick={() => setPopupStep(3)}
                    className="mt-2 w-full h-10 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-sm rounded-xl transition-all shadow-sm flex items-center justify-center gap-1"
                  >
                    Continue to Dates
                  </button>
                </div>
              )}

              {popupStep === 3 && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3">
                    <label className="text-stone-700 text-sm font-semibold">
                      Operational Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-stone-400 text-[10px] uppercase font-bold tracking-wider">
                          From
                        </span>
                        <input
                          type="date"
                          value={popupFilters.fromDate}
                          min={oneYearAgoMin}
                          max={todayMax}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (
                              val &&
                              (val < oneYearAgoMin || val > todayMax)
                            ) {
                              toast.error("Date must be within the past year");
                              return;
                            }
                            setPopupFilters((prev) => ({
                              ...prev,
                              fromDate: val,
                            }));
                            setFilters((prev) => ({
                              ...prev,
                              fromDate: val,
                            }));
                          }}
                          className="h-10 px-3 border border-stone-200 rounded-xl text-sm font-medium text-stone-800 focus:outline-indigo-500"
                          aria-label="Start date"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-stone-400 text-[10px] uppercase font-bold tracking-wider">
                          To
                        </span>
                        <input
                          type="date"
                          value={popupFilters.toDate}
                          min={oneYearAgoMin}
                          max={todayMax}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (
                              val &&
                              (val < oneYearAgoMin || val > todayMax)
                            ) {
                              toast.error("Date must be within the past year");
                              return;
                            }
                            setPopupFilters((prev) => ({
                              ...prev,
                              toDate: val,
                            }));
                            setFilters((prev) => ({
                              ...prev,
                              toDate: val,
                            }));
                          }}
                          className="h-10 px-3 border border-stone-200 rounded-xl text-sm font-medium text-stone-800 focus:outline-indigo-500"
                          aria-label="End date"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => setPopupStep(1)}
                      className="flex-1 h-10 border border-stone-200 text-stone-600 font-medium text-sm rounded-xl hover:bg-stone-50 transition-all"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handlePopupSubmit}
                      className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl transition-all shadow-sm flex items-center justify-center"
                    >
                      Launch Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* Top Header Region */}
      <div className="w-full px-8 py-6 flex items-end justify-between gap-4 border-b border-stone-100">
        <div>
          <h1
            style={{
              fontFamily: OUTFIT,
              fontSize: 22,
              fontWeight: 600,
              color: "#1a1917",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
            }}
          >
            Know Your Vehicle
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-9 px-3.5 py-2 bg-white rounded-xl outline outline-[0.80px] outline-offset-[-0.80px] outline-stone-200 inline-flex justify-start items-center gap-3">
            <div className="size-3 relative overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
              >
                <g clipPath="url(#clip0_6_8780)">
                  <path
                    d="M6.82294 11.8065C7.83044 10.9366 10.8307 8.11991 10.8307 5.41536C10.8307 4.26609 10.3742 3.16389 9.56153 2.35124C8.74887 1.53858 7.64667 1.08203 6.4974 1.08203C5.34812 1.08203 4.24592 1.53858 3.43327 2.35124C2.62061 3.16389 2.16406 4.26609 2.16406 5.41536C2.16406 8.11991 5.16435 10.9366 6.17185 11.8065C6.26571 11.8771 6.37996 11.9152 6.4974 11.9152C6.61483 11.9152 6.72908 11.8771 6.82294 11.8065Z"
                    stroke="#A8A29E"
                    strokeWidth="1.08333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.5 7.04297C7.39746 7.04297 8.125 6.31543 8.125 5.41797C8.125 4.52051 7.39746 3.79297 6.5 3.79297C5.60254 3.79297 4.875 4.52051 4.875 5.41797C4.875 6.31543 5.60254 7.04297 6.5 7.04297Z"
                    stroke="#A8A29E"
                    strokeWidth="1.08333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6_8780">
                    <rect width="13" height="13" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>

            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="bg-transparent text-stone-900 text-base font-medium font-['Outfit'] leading-5 focus:outline-none cursor-pointer appearance-none pr-1"
              aria-label="Select city"
            >
              <option value="">Select City</option>
              {Object.entries(cities || {}).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="#A8A29E"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {city && (
            <div className="h-9 px-3 bg-stone-100 rounded-xl inline-flex items-center justify-center border border-stone-200 animate-fadeIn">
              <span className="text-stone-600 text-xs font-semibold uppercase tracking-wider font-['Outfit']">
                {getCityHubLabel(city)}
              </span>
            </div>
          )}
          

          <div className="h-9 bg-white rounded-xl outline outline-[0.80px] outline-stone-200 flex items-center overflow-hidden px-2 gap-1.5">
            <input
              type="date"
              value={filters.fromDate}
              min={oneYearAgoMin}
              max={todayMax}
              onChange={(e) => {
                const val = e.target.value;
                if (val && (val < oneYearAgoMin || val > todayMax)) {
                  toast.error("Date must be within the past year");
                  return;
                }
                setFilters((prev) => ({ ...prev, fromDate: val }));
              }}
              className="bg-transparent text-stone-700 text-xs font-medium font-['Outfit'] focus:outline-none cursor-pointer"
              aria-label="Filter start date"
            />
            <span className="text-stone-300 text-xs px-0.5">to</span>
            <input
              type="date"
              value={filters.toDate}
              min={oneYearAgoMin}
              max={todayMax}
              onChange={(e) => {
                const val = e.target.value;
                if (val && (val < oneYearAgoMin || val > todayMax)) {
                  toast.error("Date must be within the past year");
                  return;
                }
                setFilters((prev) => ({ ...prev, toDate: val }));
              }}
              className="bg-transparent text-stone-700 text-xs font-medium font-['Outfit'] focus:outline-none cursor-pointer"
              aria-label="Filter end date"
            />
          </div>

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || vehicleLoading}
            sx={{
              textTransform: "capitalize",
              fontFamily: OUTFIT,
              borderRadius: "12px",
              height: "36px",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Search
          </Button>
          <button
            type="button"
            onClick={() => {
              setKeyType("");
              setVehicleId(null);
              setSelectedVehicle(null);
              setVehicleApiData([]);
              setRows([]);
              setColumns([]);
              setCity("");
              setVehicleUtilizationData(null);
              setApiData([]);
              setFilters({
                fromDate: getOneYearAgo(),
                toDate: getToday(),
                calculationType: "vehicleIssues",
              });
              setEntityCounts({
                bookingCount: 0,
                documentCount: 0,
                issueCount: 0,
                fuelLogCount: 0,
                workOrderCount: 0,
              });
              setPaginationModel({ page: 0, pageSize: 10 });
              toast.success("All filters cleared", {
                position: "bottom-center",
              });
            }}
            className="h-8 px-3 rounded-lg border border-stone-300 bg-white text-xs font-medium text-stone-700 transition hover:bg-stone-50 mb-1.5 ml-2"
            aria-label="Clear all filters"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Filter Control Option Strip - Vehicle Search */}
      <div className="w-full bg-[#FAFAFA] border-b border-stone-200/80 px-8 py-4 flex items-center justify-between gap-4">
        <div className="w-[520px] inline-flex justify-start items-center gap-3 relative">
          <Autocomplete
            fullWidth
            disabled={!city}
            options={vehicleApiData}
            getOptionLabel={(option) =>
              option ? `${option.name} – ${option.licensePlate}` : ""
            }
            value={selectedVehicle}
            onChange={(event, newValue) => {
              setVehicleId(newValue ? newValue.id : null);
              setSelectedVehicle(newValue);
              setKeyType("");
              setRows([]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  !city
                    ? "⚠️ Choose a City First..."
                    : vehicleLoading
                      ? "Loading vehicles..."
                      : "Type to search vehicle name or plate..."
                }
                variant="outlined"
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: OUTFIT,
                    color: "#1A1917",
                    fontSize: "15px",
                    fontWeight: 500,
                    paddingLeft: "14px !important",
                    paddingRight: "45px !important",
                    height: "44px",
                    borderRadius: "16px",
                    "& fieldset": {
                      borderColor: "#E7E5E4",
                      borderWidth: "0.80px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#D6D3D1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#4F46E5",
                      borderWidth: "1.5px",
                    },
                  },
                }}
              />
            )}
          />

          {city && vehicleApiData.length > 0 && (
            <div className="size-8 absolute right-10 bg-stone-100 rounded-[10px] flex-shrink-0 flex items-center justify-center pointer-events-none z-20 shadow-2xs">
              <div className="text-center text-stone-500 text-xs font-semibold font-['Outfit']">
                {vehicleApiData.length}
              </div>
            </div>
          )}
        </div>

       {selectedVehicle && (
  <div className="flex items-center gap-4 h-11">
    <span
      style={{
        color: "#A8A29E",
        fontFamily: "Outfit",
        fontSize: "13px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "19.5px",
      }}
    >
      {cities[city] || "Selected City"} Fleet
    </span>
    <div className="w-px h-4 relative bg-stone-200" />
    
    {/* Add vehicle number display with copy button */}
    <div className="flex items-center gap-2">
      <span className="text-stone-700 text-sm font-medium font-['Outfit']">
        {selectedVehicle?.licensePlate || 'N/A'}
      </span>
      <button
        onClick={copyVehicleNumber}
        className="p-1 hover:bg-stone-100 rounded-md transition-colors"
        title="Copy vehicle number"
        aria-label="Copy vehicle number"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-stone-400 hover:text-stone-600"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    
    <div className="w-px h-4 relative bg-stone-200" />
    
    {(() => {
      const currentStatus =
        VehicleUtilizationData?.costingAndProfitability?.status;
      if (currentStatus) {
        const statusStyles = getStatusStyles(currentStatus);
        return (
          <div
            className={`rounded-full border text-xs font-semibold font-['Outfit'] ${statusStyles.bg} ${statusStyles.text} ${statusStyles.border}`}
            style={{
              display: "flex",
              padding: "2px 8px",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span
              className={`h-2 w-2 rounded-full flex-shrink-0 inline-block ${statusStyles.dotBg}`}
            />
            {statusStyles.label}
          </div>
        );
      } else {
        return (
          <div
            className="rounded-full border text-xs font-semibold font-['Outfit'] bg-stone-50 text-stone-500 border-stone-200"
            style={{
              display: "flex",
              padding: "2px 8px",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span className="h-2 w-2 rounded-full flex-shrink-0 inline-block bg-stone-400" />
            No Status Available
          </div>
        );
      }
    })()}
  </div>
)}
      </div>

      <div className="w-full px-6 bg-white">
        <VehicleTrendGrid
          city={city}
          vehicleId={vehicleId}
          filters={filters}
          totalCount={totalCountForTrend}
          setTotalCount={setTotalCountForTrend}
          setVehicleTrendGridApi={setVehicleTrendGridApi}
          vehicleTrendGridApi={vehicleTrendGridApi}
          getVehicleTrendData={getVehicleTrendData}
        />
      </div>

      <div className="w-full flex flex-row justify-start items-start gap-6 px-6 bg-white">
        <div className="w-[25%] flex-shrink-0">
          <div className="w-full bg-white flex flex-col gap-4">
            {VehicleUtilizationData ? (
              <div className="w-full">
                <VehicleUtilizationChart data={VehicleUtilizationData} />
                {/* VEHICLE STATS LABEL LIST - USING ENTITY COUNTS */}
                <div className="w-full flex flex-col gap-2 p-4 bg-white border border-stone-200 rounded-xl max-w-sm shadow-sm mt-4">
                  {[
                    {
                      id: "vehicleBookings",
                      label: "Bookings",
                      count: entityCounts.bookingCount,
                      colors: {
                        bg: "bg-indigo-50/60",
                        icon: "#4F46E5",
                        text: "text-indigo-600",
                        badge: "bg-indigo-50 text-indigo-600",
                      },
                      icon: (strokeHex) => (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 11 11"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <g clipPath="url(#clip_b1)">
                            <path
                              d="M8.70573 7.79036H9.6224C9.8974 7.79036 10.0807 7.60703 10.0807 7.33203V5.95703C10.0807 5.54453 9.7599 5.17786 9.39323 5.0862C8.56823 4.85703 7.33073 4.58203 7.33073 4.58203C7.33073 4.58203 6.7349 3.94036 6.3224 3.52786C6.09323 3.34453 5.81823 3.20703 5.4974 3.20703H2.28906C2.01406 3.20703 1.7849 3.39036 1.6474 3.61953L1.00573 4.9487V7.33203H2.28906"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="3.2"
                              cy="7.8"
                              r="1"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                            />
                            <circle
                              cx="7.8"
                              cy="7.8"
                              r="1"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip_b1)">
                              <rect width="11" height="11" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ),
                    },
                    {
                      id: "vehicleIssues",
                      label: "Issues",
                      count: entityCounts.issueCount,
                      colors: {
                        bg: "bg-red-50",
                        icon: "#EF4444",
                        text: "text-red-600",
                        badge: "bg-red-50 text-red-600",
                      },
                      icon: (strokeHex) => (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 11 11"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <g clipPath="url(#clip_i1)">
                            <path
                              d="M6.73811 2.88657C6.65413 2.97224 6.60709 3.08743 6.60709 3.2074C6.60709 3.32737 6.65413 3.44256 6.73811 3.52823L7.47144 4.26157C7.55711 4.34554 7.6723 4.39258 7.79227 4.39258C7.91224 4.39258 8.02743 4.34554 8.11311 4.26157L9.84102 2.53365C10.0715 3.04294 10.1413 3.61038 10.0411 4.16034C9.94086 4.7103 9.67543 5.21666 9.28015 5.61194C8.88487 6.00723 8.37851 6.27265 7.82855 6.37286C7.27859 6.47306 6.71115 6.40328 6.20186 6.17282L3.03477 9.3399C2.85244 9.52223 2.60513 9.62467 2.34727 9.62467C2.08941 9.62467 1.84211 9.52223 1.65977 9.3399C1.47744 9.15756 1.375 8.91026 1.375 8.6524C1.375 8.39454 1.47744 8.14723 1.65977 7.9649L4.82686 4.79782C4.59639 4.28852 4.52661 3.72108 4.62681 3.17112C4.72702 2.62116 4.99245 2.1148 5.38773 1.71952C5.78301 1.32424 6.28937 1.05881 6.83933 0.958605C7.38929 0.858401 7.95673 0.928182 8.46602 1.15865L6.74269 2.88198L6.73811 2.88657Z"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip_i1)">
                              <rect width="11" height="11" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ),
                    },
                    {
                      id: "vehicleDocuments",
                      label: "Documents",
                      count: entityCounts.documentCount,
                      colors: {
                        bg: "bg-cyan-50/60",
                        icon: "#0891B2",
                        text: "text-cyan-700",
                        badge: "bg-cyan-50 text-cyan-600",
                      },
                      icon: (strokeHex) => (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 11 11"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <g clipPath="url(#clip_d1)">
                            <path
                              d="M6.8776 0.917969H2.7526C2.50949 0.917969 2.27633 1.01455 2.10442 1.18645C1.93251 1.35836 1.83594 1.59152 1.83594 1.83464V9.16797C1.83594 9.41108 1.93251 9.64424 2.10442 9.81615C2.27633 9.98806 2.50949 10.0846 2.7526 10.0846H8.2526C8.49572 10.0846 8.72888 9.98806 8.90079 9.81615C9.07269 9.64424 9.16927 9.41108 9.16927 9.16797V3.20964L6.8776 0.917969Z"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.41406 0.917969V2.7513C6.41406 2.99442 6.51064 3.22758 6.68255 3.39948C6.85446 3.57139 7.08761 3.66797 7.33073 3.66797H9.16406"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M4.58073 4.125H3.66406"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7.33073 5.95703H3.66406"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7.33073 7.79297H3.66406"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip_d1)">
                              <rect width="11" height="11" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ),
                    },
                    {
                      id: "fuelLogs",
                      label: "Fuel History",
                      count: entityCounts.fuelLogCount,
                      colors: {
                        bg: "bg-emerald-50/60",
                        icon: "#059669",
                        text: "text-emerald-700",
                        badge: "bg-emerald-50 text-emerald-600",
                      },
                      icon: (strokeHex) => (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 11 11"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <g clipPath="url(#clip_f1)">
                            <path
                              d="M1.375 10.082H6.875"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M1.83594 4.125H6.41927"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.41927 10.0846V1.83464C6.41927 1.59152 6.32269 1.35836 6.15079 1.18645C5.97888 1.01455 5.74572 0.917969 5.5026 0.917969H2.7526C2.50949 0.917969 2.27633 1.01455 2.10442 1.18645C1.93251 1.35836 1.83594 1.59152 1.83594 1.83464V10.0846"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.41406 5.95964H7.33073C7.57384 5.95964 7.807 6.05621 7.97891 6.22812C8.15082 6.40003 8.2474 6.63319 8.2474 6.8763V7.79297C8.2474 8.03608 8.34397 8.26924 8.51588 8.44115C8.68779 8.61306 8.92095 8.70964 9.16406 8.70964C9.40718 8.70964 9.64034 8.61306 9.81224 8.44115C9.98415 8.26924 10.0807 7.79297V4.50672C10.0808 4.38576 10.057 4.26598 10.0106 4.15428C9.96416 4.04257 9.8961 3.94116 9.81031 3.85589L8.2474 2.29297"
                              stroke={strokeHex}
                              strokeWidth="1.0"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip_f1)">
                              <rect width="11" height="11" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-xl flex items-center justify-center ${item.colors.bg}`}
                        >
                          {item.icon(item.colors.icon)}
                        </div>
                        <span className="text-stone-700 text-sm font-medium font-['Outfit'] leading-5">
                          {item.label}
                        </span>
                      </div>
                      <div
                        className={`px-3 py-1 min-w-[36px] h-6 rounded-xl border border-stone-100/80 inline-flex items-center justify-center shadow-sm ${item.colors.badge}`}
                      >
                        <span className="text-center text-xs font-semibold font-['Outfit'] leading-4">
                          {item.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="w-full border border-dashed border-stone-200 rounded-xl p-6 text-center text-stone-400 text-xs">
                Select vehicle to compute analytics metrics view
              </div>
            )}
          </div>
        </div>

        <div className="w-[73%] flex-shrink-0">
          <div className="w-full flex flex-col">
            {/* Tab Strip */}
            <div className="flex items-end gap-0.5 border-b border-stone-200">
              {[
                {
                  id: "vehicleBookings",
                  label: "Bookings",
                  count: entityCounts.bookingCount,
                  icon: (strokeHex) => (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 11 11"
                      fill="none"
                      className="flex-shrink-0 relative translate-y-[0.5px]"
                    >
                      <g clipPath="url(#clip0_59_9397)">
                        <path
                          d="M8.70573 7.79036H9.6224C9.8974 7.79036 10.0807 7.60703 10.0807 7.33203V5.95703C10.0807 5.54453 9.7599 5.17786 9.39323 5.0862C8.56823 4.85703 7.33073 4.58203 7.33073 4.58203C7.33073 4.58203 6.7349 3.94036 6.3224 3.52786C6.09323 3.34453 5.81823 3.20703 5.4974 3.20703H2.28906C2.01406 3.20703 1.7849 3.39036 1.6474 3.61953L1.00573 4.9487C0.945037 5.12572 0.914062 5.31156 0.914062 5.4987V7.33203H2.28906"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3.20573 8.70833C3.71199 8.70833 4.1224 8.29793 4.1224 7.79167C4.1224 7.79167 3.71199 6.875 3.20573 6.875C2.69947 6.875 2.28906 7.28541 2.28906 7.79167C2.28906 8.29793 2.69947 8.70833 3.20573 8.70833Z"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4.125 7.79297H6.875"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.79167 8.70833C8.29793 8.70833 8.70833 8.29793 8.70833 7.79167C8.70833 7.28541 8.29793 6.875 7.79167 6.875C7.28541 6.875 6.875 7.28541 6.875 7.79167C6.875 8.29793 7.28541 8.70833 7.79167 8.70833Z"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_59_9397)">
                          <rect width="11" height="11" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                },
                {
                  id: "vehicleDocuments",
                  label: "Documents",
                  count: entityCounts.documentCount,
                  icon: (strokeHex) => (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 11 11"
                      fill="none"
                      className="flex-shrink-0 relative translate-y-[0.5px]"
                    >
                      <g clipPath="url(#clip0_59_9408)">
                        <path
                          d="M6.8776 0.917969H2.7526C2.50949 0.917969 2.27633 1.01455 2.10442 1.18645C1.93251 1.35836 1.83594 1.59152 1.83594 1.83464V9.16797C1.83594 9.41108 1.93251 9.64424 2.10442 9.81615C2.27633 9.98806 2.50949 10.0846 2.7526 10.0846H8.2526C8.49572 10.0846 8.72888 9.98806 8.90079 9.81615C9.07269 9.64424 9.16927 9.41108 9.16927 9.16797V3.20964L6.8776 0.917969Z"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.41406 0.917969V2.7513C6.41406 2.99442 6.51064 3.22758 6.68255 3.39948C6.85446 3.57139 7.08761 3.66797 7.33073 3.66797H9.16406"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4.58073 4.125H3.66406"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.33073 5.95703H3.66406"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.33073 7.79297H3.66406"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_59_9408)">
                          <rect width="11" height="11" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                },
                {
                  id: "vehicleIssues",
                  label: "Issues",
                  count: entityCounts.issueCount,
                  icon: (strokeHex) => (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 11 11"
                      fill="none"
                      className="flex-shrink-0 relative translate-y-[0.5px]"
                    >
                      <g clipPath="url(#clip0_59_9420)">
                        <path
                          d="M6.73811 2.88657C6.65413 2.97224 6.60709 3.08743 6.60709 3.2074C6.60709 3.32737 6.65413 3.44256 6.73811 3.52823L7.47144 4.26157C7.55711 4.34554 7.6723 4.39258 7.79227 4.39258C7.91224 4.39258 8.02743 4.34554 8.11311 4.26157L9.84102 2.53365C10.0715 3.04294 10.1413 3.61038 10.0411 4.16034C9.94086 4.7103 9.67543 5.21666 9.28015 5.61194C8.88487 6.00723 8.37851 6.27265 7.82855 6.37286C7.27859 6.47306 6.71115 6.40328 6.20186 6.17282L3.03477 9.3399C2.85244 9.52223 2.60513 9.62467 2.34727 9.62467C2.08941 9.62467 1.84211 9.52223 1.65977 9.3399C1.47744 9.15756 1.375 8.91026 1.375 8.6524C1.375 8.39454 1.47744 8.14723 1.65977 7.9649L4.82686 4.79782C4.59639 4.28852 4.52661 3.72108 4.62681 3.17112C4.72702 2.62116 4.99245 2.1148 5.38773 1.71952C5.78301 1.32424 6.28937 1.05881 6.83933 0.958605C7.38929 0.858401 7.95673 0.928182 8.46602 1.15865L6.74269 2.88198L6.73811 2.88657Z"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_59_9420">
                          <rect width="11" height="11" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                },
                {
                  id: "fuelLogs",
                  label: "Fuel History",
                  count: entityCounts.fuelLogCount,
                  icon: (strokeHex) => (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 11 11"
                      fill="none"
                      className="flex-shrink-0 relative translate-y-[0.5px]"
                    >
                      <g clipPath="url(#clip0_59_9428)">
                        <path
                          d="M1.375 10.082H6.875"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1.83594 4.125H6.41927"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.41927 10.0846V1.83464C6.41927 1.59152 6.32269 1.35836 6.15079 1.18645C5.97888 1.01455 5.74572 0.917969 5.5026 0.917969H2.7526C2.50949 0.917969 2.27633 1.01455 2.10442 1.18645C1.93251 1.35836 1.83594 1.59152 1.83594 1.83464V10.0846"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.41406 5.95964H7.33073C7.57384 5.95964 7.807 6.05621 7.97891 6.22812C8.15082 6.40003 8.2474 6.63319 8.2474 6.8763V7.79297C8.2474 8.03608 8.34397 8.26924 8.51588 8.44115C8.68779 8.61306 8.92095 8.70964 9.16406 8.70964C9.40718 8.70964 9.64034 8.61306 9.81224 8.44115C9.98415 8.26924 10.0807 7.79297V4.50672C10.0808 4.38576 10.057 4.26598 10.0106 4.15428C9.96416 4.04257 9.8961 3.94116 9.81031 3.85589L8.2474 2.29297"
                          stroke={strokeHex}
                          strokeWidth="1.0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_59_9428">
                          <rect width="11" height="11" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                },
              ].map((tab) => {
                const isActive = keyType === tab.id;

                let tabBg = isActive ? "bg-white" : "bg-transparent";
                let tabBorder = isActive
                  ? "border-stone-200 border-b-white"
                  : "border-transparent";
                let iconStrokeHex = isActive ? "#4F46E5" : "#A8A29E";
                let textColor = isActive
                  ? "text-stone-900 font-semibold"
                  : "text-stone-400 font-normal";
                let countBg = isActive
                  ? "bg-indigo-50 text-indigo-600"
                  : "bg-gray-200 text-stone-400";
                let cursorStyle = "cursor-pointer";

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setKeyType(tab.id);
                      setPaginationModel({ page: 0, pageSize: 10 });
                    }}
                    className={`h-10 px-4 py-2.5 transition-all outline-none border-l-[0.80px] border-r-[0.80px] border-t-[1.60px] border-b-[0.80px] rounded-tl-xl rounded-tr-xl inline-flex justify-start items-center gap-2 relative top-[0.80px] z-10 ${tabBg} ${tabBorder} ${cursorStyle}`}
                    aria-label={`View ${tab.label}`}
                  >
                    {tab.icon(iconStrokeHex)}

                    <span
                      className={`text-center text-xs font-['Outfit'] leading-5 select-none ${textColor}`}
                    >
                      {tab.label}
                    </span>

                    <div
                      className={`px-1.5 h-4 min-w-6 rounded-sm inline-flex items-center justify-center ${countBg}`}
                    >
                      <span className="text-center text-[10px] font-medium font-['Outfit'] leading-4">
                        {tab.count}
                      </span>
                    </div>
                  </button>
                );
              })}

              {/* Spacer */}
              <div className="flex-1" />
            </div>

            {/* Logs Content */}
            <div className="w-full pt-4">
              {keyType && rows.length > 0 && (
                <div className="w-full">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, fontFamily: OUTFIT }}
                  >
                    {getGridTitle()}
                  </Typography>

                  <FigmaDataWorkspace
                    rows={rows}
                    columns={columns}
                    totalCount={totalCount}
                    loading={loading}
                    keyType={keyType}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    handleViewClick={handleViewClick}
                  />
                </div>
              )}

              {keyType && rows.length === 0 && !loading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "250px",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontFamily: OUTFIT }}
                  >
                    {keyType === "vehicleBookings" && "No Bookings Found"}
                    {keyType === "vehicleDocuments" && "No Documents Found"}
                    {keyType === "vehicleIssues" && "No Issues Found"}
                    {keyType === "fuelLogs" && "No Fuel History Found"}
                  </Typography>
                </Box>
              )}

              {!keyType && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "250px",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontFamily: OUTFIT }}
                  >
                    Select a tab to view logs
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYV;
